import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Cpu, Users as UsersIcon, Radio, Save, Mail, Check, Info, Plus, Minus as MinusIcon,
  Sparkles, Infinity as InfinityIcon, Server, LineChart, Wifi, Database,
  RotateCcw, Clock,
} from 'lucide-react';
import { useCurrency } from '../context/CurrencyContext';
import { useAuth } from '../context/AuthContext';
import {
  PTI_ASSETS_TIERS,
  PTI_ASSETS_PACK,
  PTI_ASSETS_PROTOCOLS,
  PTI_ASSETS_CONSULTING,
  PTI_ASSETS_ANNUAL_DISCOUNT,
} from '../mock/ptiAssetsData';

const ProtocolIcon = ({ id, className }) => {
  if (id === 'opcua') return <Cpu className={className} />;
  if (id === 'mqtt') return <Wifi className={className} />;
  if (id === 'sqldb') return <Database className={className} />;
  return <Radio className={className} />;
};

const Section = ({ children, className = '' }) => (
  <div className={`rounded-2xl border border-white/10 bg-white/[0.02] p-6 md:p-7 ${className}`}>{children}</div>
);

const TierCard = ({ tier, selected, onSelect, formatMoney }) => {
  const isCustom = tier.price === null;
  return (
  <button
    type="button"
    onClick={() => onSelect(tier.id)}
    className={`relative text-left rounded-xl p-5 border transition-all duration-200 group
      ${selected
        ? 'border-cyan-400/60 bg-cyan-400/[0.05] shadow-[0_0_0_1px_rgba(34,211,238,0.35),0_10px_40px_-10px_rgba(34,211,238,0.30)]'
        : 'border-white/10 bg-white/[0.02] hover:border-white/20 hover:bg-white/[0.04]'
      }`}
  >
    {selected && (
      <span className="absolute top-3 right-3 h-5 w-5 rounded-full bg-cyan-400 text-black flex items-center justify-center">
        <Check className="w-3 h-3" strokeWidth={3} />
      </span>
    )}
    {tier.popular && !selected && (
      <span className="absolute -top-2 right-3 text-[9px] tracking-[0.18em] font-semibold uppercase px-2 py-0.5 rounded-full bg-cyan-400/90 text-black">
        Most popular
      </span>
    )}
    <div className="text-[10px] tracking-[0.18em] font-semibold uppercase text-white/50">{tier.label}</div>
    <div className="mt-3 text-xl md:text-[22px] leading-tight font-semibold text-white">{tier.name}</div>
    <div className="mt-3 flex items-baseline gap-1">
      {isCustom ? (
        <span className="text-emerald-400 text-lg font-semibold">Custom</span>
      ) : (
        <>
          <span className="text-white text-xl font-semibold tracking-tight">{formatMoney(tier.price)}</span>
          <span className="text-white/50 text-xs">/mo</span>
        </>
      )}
    </div>
    <div className="mt-4 space-y-1 text-[12px] text-white/65">
      <div className="flex items-center gap-2">
        <Server className="w-3 h-3 text-cyan-300" />
        {tier.minAssets}–{tier.maxAssets === 'Unlimited' ? (
          <span className="inline-flex items-center gap-1 text-emerald-300"><InfinityIcon className="w-3 h-3" /> Unlimited</span>
        ) : tier.maxAssets} assets
      </div>
      <div className="flex items-center gap-2">
        <UsersIcon className="w-3 h-3 text-cyan-300" />
        {tier.users} users included
      </div>
      <div className="flex items-center gap-2 text-white/45">
        <LineChart className="w-3 h-3" />
        ≈ {typeof tier.tagsEquivalent === 'number' ? tier.tagsEquivalent.toLocaleString('en-US') : tier.tagsEquivalent} tags equivalent
      </div>
    </div>
    <p className="mt-3 text-[12.5px] leading-relaxed text-white/55">{tier.tagline}</p>
  </button>
  );
};

const ProtocolChip = ({ p, selected, onToggle }) => (
  <button
    type="button"
    onClick={() => onToggle(p.id)}
    className={`text-left rounded-xl border p-4 transition-colors ${
      selected
        ? 'border-cyan-400/60 bg-cyan-400/[0.06]'
        : 'border-white/10 bg-white/[0.02] hover:border-white/20 hover:bg-white/[0.04]'
    }`}
  >
    <div className="flex items-start gap-3">
      <span className={`h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
        selected ? 'bg-cyan-400/15 border border-cyan-400/40' : 'bg-white/5 border border-white/10'
      }`}>
        <ProtocolIcon id={p.id} className={`w-4 h-4 ${selected ? 'text-cyan-300' : 'text-white/70'}`} />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-white font-semibold text-[14.5px]">{p.name}</span>
          {selected && <Check className="w-3.5 h-3.5 text-cyan-300" strokeWidth={3} />}
        </div>
        <div className="mt-0.5 text-[12px] text-white/55">{p.description}</div>
      </div>
    </div>
  </button>
);

const Stepper = ({ label, sublabel, value, onChange, min = 0, max = 100, packUnit }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 flex items-center gap-4">
    <div className="flex-1">
      <div className="text-white font-semibold text-[14px]">{label}</div>
      <div className="mt-0.5 text-[12px] text-white/55">{sublabel}</div>
    </div>
    <div className="flex items-center gap-2 flex-shrink-0">
      <button onClick={() => onChange(Math.max(min, value - 1))} disabled={value <= min}
        className="h-9 w-9 rounded-lg bg-white/[0.04] hover:bg-white/[0.10] disabled:opacity-30 disabled:cursor-not-allowed border border-white/10 text-white flex items-center justify-center">
        <MinusIcon className="w-3.5 h-3.5" />
      </button>
      <div className="w-16 text-center">
        <div className="text-white font-semibold text-lg tabular-nums leading-none">{value}</div>
        {packUnit && <div className="text-[10px] text-white/45 uppercase tracking-wider mt-0.5">{packUnit}</div>}
      </div>
      <button onClick={() => onChange(Math.min(max, value + 1))} disabled={value >= max}
        className="h-9 w-9 rounded-lg bg-white/[0.04] hover:bg-white/[0.10] disabled:opacity-30 disabled:cursor-not-allowed border border-white/10 text-white flex items-center justify-center">
        <Plus className="w-3.5 h-3.5" />
      </button>
    </div>
  </div>
);

const PtiAssetsMode = () => {
  const { formatMoney } = useCurrency();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [selectedTier, setSelectedTier] = useState('rt_growth');
  const [protocols, setProtocols] = useState({ opcua: true, mqtt: false, sqldb: false });
  const [assetPacks, setAssetPacks] = useState(0);
  const [userPacks, setUserPacks] = useState(0);
  const [billing, setBilling] = useState('monthly');
  const [includeConsulting, setIncludeConsulting] = useState(false);

  const tier = PTI_ASSETS_TIERS.find((t) => t.id === selectedTier);
  const isCustom = tier.price === null;
  const selectedProtocols = Object.keys(protocols).filter((k) => protocols[k]);
  const toggleProtocol = (id) => setProtocols({ ...protocols, [id]: !protocols[id] });

  const totalAssets = useMemo(() => {
    if (tier.maxAssets === 'Unlimited') return 'Unlimited';
    return tier.maxAssets + assetPacks * PTI_ASSETS_PACK.assetsPerPack;
  }, [tier, assetPacks]);
  const totalUsers = tier.users + userPacks * PTI_ASSETS_PACK.usersPerPack;

  const monthly = useMemo(() => {
    if (isCustom) return 0;
    return tier.price
      + assetPacks * PTI_ASSETS_PACK.assetPackPrice
      + userPacks * PTI_ASSETS_PACK.userPackPrice;
  }, [tier, assetPacks, userPacks, isCustom]);

  const displayMonthly = billing === 'annual' ? monthly * (1 - PTI_ASSETS_ANNUAL_DISCOUNT) : monthly;
  const annual = displayMonthly * 12;
  const consultingTotal = includeConsulting ? PTI_ASSETS_CONSULTING.price : 0;
  const totalYearOne = annual + consultingTotal;

  const reset = () => {
    setSelectedTier('rt_growth');
    setProtocols({ opcua: true, mqtt: false, sqldb: false });
    setAssetPacks(0);
    setUserPacks(0);
    setIncludeConsulting(false);
  };

  const proceedToQuote = () => {
    if (selectedProtocols.length === 0) {
      alert('Please select at least one real-time protocol.');
      return;
    }
    const state = {
      product: 'pti',
      licensing_mode: 'assets_users',
      tier_id: tier.id,
      tier_name: `PTI Realtime — ${tier.name}`,
      tier_price: monthly,
      billing,
      consulting: consultingTotal,
      extra_lines: [
        ...(isCustom ? [{
          type: 'tier', sku: tier.id,
          description: `PTI Realtime — ${tier.name} (Custom · ${tier.minAssets}+ assets, ${tier.users}+ users)`,
          qty: 1, unit_price: 0, recurring: 'monthly',
        }] : [{
          type: 'tier', sku: tier.id,
          description: `PTI Realtime — ${tier.name} (${tier.minAssets}–${tier.maxAssets} assets, ${tier.users} users)`,
          qty: 12, unit_price: displayMonthly, recurring: 'monthly',
        }]),
        ...(assetPacks > 0 && !isCustom ? [{
          type: 'addon', sku: 'asset_pack_10',
          description: `+${assetPacks * PTI_ASSETS_PACK.assetsPerPack} additional assets (${assetPacks} × pack of 10)`,
          qty: 12 * assetPacks, unit_price: PTI_ASSETS_PACK.assetPackPrice, recurring: 'monthly',
        }] : []),
        ...(userPacks > 0 && !isCustom ? [{
          type: 'addon', sku: 'user_pack_10',
          description: `+${userPacks * PTI_ASSETS_PACK.usersPerPack} additional users (${userPacks} × pack of 10)`,
          qty: 12 * userPacks, unit_price: PTI_ASSETS_PACK.userPackPrice, recurring: 'monthly',
        }] : []),
        ...(includeConsulting ? [{
          type: 'service', sku: 'onboarding_consulting',
          description: `Onboarding & configuration consulting — ${PTI_ASSETS_CONSULTING.weeks} weeks (flat, optional)`,
          qty: 1, unit_price: PTI_ASSETS_CONSULTING.price, recurring: 'one_time',
        }] : []),
        {
          type: 'custom', sku: 'protocols',
          description: `Real-time protocols included: ${selectedProtocols.map((p) => PTI_ASSETS_PROTOCOLS.find((x) => x.id === p).name).join(', ')}`,
          qty: 1, unit_price: 0, recurring: 'one_time',
        },
      ],
    };
    if (!user) navigate('/login', { state: { from: { pathname: '/admin/quotes/new' } } });
    else navigate('/admin/quotes/new', { state });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-6 lg:gap-8">
      {/* LEFT — configurator */}
      <div className="space-y-6">
        {/* Choose tier */}
        <Section>
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-xl font-semibold text-white">Choose an asset & user bracket</h2>
            <button onClick={reset} className="flex items-center gap-1.5 text-[11px] tracking-[0.18em] font-semibold uppercase text-white/60 hover:text-white transition-colors">
              <RotateCcw className="w-3.5 h-3.5" /> Reset
            </button>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            {PTI_ASSETS_TIERS.map((t) => (
              <TierCard key={t.id} tier={t} selected={selectedTier === t.id} onSelect={setSelectedTier} formatMoney={formatMoney} />
            ))}
          </div>

          <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatTile icon={Server} label="Assets range" value={tier.maxAssets === 'Unlimited' ? '501+' : `${tier.minAssets}–${tier.maxAssets}`} />
            <StatTile icon={UsersIcon} label="Users included" value={tier.users} />
            <StatTile icon={Radio} label="Protocols selected" value={selectedProtocols.length || '—'} />
            <StatTile icon={Clock} label="Consulting" value={includeConsulting ? '5 wks' : 'Off'} />
          </div>
        </Section>

        {/* Protocols */}
        <Section>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold text-white">Real-time protocols</h3>
            <div className="text-[11px] tracking-[0.15em] font-semibold uppercase text-white/50">Pick one or more</div>
          </div>
          <p className="text-[12.5px] text-white/55 leading-relaxed">
            This SaaS licensing model streams from <span className="text-cyan-300 font-semibold">real-time sources only</span>.
            Historians and file/batch sources aren't supported — use the Hybrid Concurrent model for those.
          </p>
          <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3">
            {PTI_ASSETS_PROTOCOLS.map((p) => (
              <ProtocolChip key={p.id} p={p} selected={protocols[p.id]} onToggle={toggleProtocol} />
            ))}
          </div>
        </Section>

        {/* Packs */}
        <Section>
          <h3 className="text-lg font-semibold text-white mb-4">Add-on packs</h3>
          <div className="space-y-3">
            <Stepper
              label={`Extra assets — packs of ${PTI_ASSETS_PACK.assetsPerPack}`}
              sublabel={`${formatMoney(PTI_ASSETS_PACK.assetPackPrice)} / pack / month · beyond your tier's ${tier.maxAssets === 'Unlimited' ? 'unlimited' : tier.maxAssets} assets`}
              value={assetPacks}
              onChange={setAssetPacks}
              min={0} max={100}
              packUnit="packs"
            />
            <Stepper
              label={`Extra users — packs of ${PTI_ASSETS_PACK.usersPerPack}`}
              sublabel={`${formatMoney(PTI_ASSETS_PACK.userPackPrice)} / pack / month · beyond the ${tier.users} included users`}
              value={userPacks}
              onChange={setUserPacks}
              min={0} max={100}
              packUnit="packs"
            />
          </div>
        </Section>

        {/* Consulting (optional) */}
        <Section>
          <div className="flex items-start gap-3">
            <span className={`h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0 border ${
              includeConsulting ? 'bg-purple-400/15 border-purple-400/40' : 'bg-white/5 border-white/10'
            }`}>
              <Clock className={`w-5 h-5 ${includeConsulting ? 'text-purple-300' : 'text-white/50'}`} />
            </span>
            <div className="flex-1">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="text-white font-semibold">Onboarding consulting</div>
                  <div className="text-[12.5px] text-white/60 mt-0.5">{PTI_ASSETS_CONSULTING.description}</div>
                </div>
                <button
                  role="switch"
                  aria-checked={includeConsulting}
                  onClick={() => setIncludeConsulting((v) => !v)}
                  className={`relative h-6 w-11 rounded-full transition-colors flex-shrink-0 ${
                    includeConsulting ? 'bg-purple-400' : 'bg-white/15'
                  }`}
                >
                  <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${
                    includeConsulting ? 'translate-x-5' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>
              <div className="mt-3 flex items-baseline gap-3">
                <span className="text-2xl font-bold text-white tabular-nums">{formatMoney(PTI_ASSETS_CONSULTING.price)}</span>
                <span className="text-white/50 text-sm">flat, one-time · {includeConsulting ? 'included in quote' : 'optional — not included'}</span>
              </div>
            </div>
          </div>
        </Section>
      </div>

      {/* RIGHT — live estimate */}
      <div>
        <aside className="rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.03] to-white/[0.01] p-6 md:p-7 sticky top-24">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[10px] tracking-[0.22em] font-semibold uppercase text-white/50">Live estimate</div>
              <div className="mt-2 text-[22px] font-semibold text-white">Realtime — {tier.name}</div>
              <div className="mt-1 text-[12px] text-white/50">
                {totalAssets === 'Unlimited' ? 'Unlimited' : totalAssets} assets · {totalUsers} users
              </div>
            </div>
            <Sparkles className="w-6 h-6 text-cyan-300" />
          </div>

          {/* Billing toggle */}
          <div className="mt-5 relative inline-flex w-full p-1 rounded-full bg-white/[0.04] border border-white/10">
            <button onClick={() => setBilling('monthly')}
              className={`flex-1 h-9 rounded-full text-[12px] font-semibold tracking-wider uppercase transition-colors ${billing === 'monthly' ? 'bg-white text-black' : 'text-white/60 hover:text-white/80'}`}>
              Monthly
            </button>
            <button onClick={() => setBilling('annual')}
              className={`flex-1 h-9 rounded-full text-[12px] font-semibold tracking-wider uppercase transition-colors flex items-center justify-center gap-2 ${billing === 'annual' ? 'bg-white text-black' : 'text-white/60 hover:text-white/80'}`}>
              Annual
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${billing === 'annual' ? 'bg-emerald-400/20 text-emerald-500' : 'bg-emerald-400/15 text-emerald-300'}`}>−15%</span>
            </button>
          </div>

          <div className="mt-6">
            <div className="text-[10px] tracking-[0.22em] font-semibold uppercase text-white/50">Monthly subscription</div>
            <div className="mt-2 flex items-baseline gap-2 flex-wrap">
              {isCustom ? (
                <span className="text-4xl font-bold text-emerald-300">Custom</span>
              ) : (
                <>
                  <span className="text-[42px] leading-none font-bold tracking-tight bg-clip-text text-transparent"
                    style={{ backgroundImage: 'linear-gradient(90deg, #22d3ee 0%, #a855f7 100%)' }}>
                    {formatMoney(displayMonthly)}
                  </span>
                  <span className="text-white/50 text-sm">/ month</span>
                </>
              )}
            </div>
          </div>

          <div className="mt-4">
            <div className="text-[10px] tracking-[0.22em] font-semibold uppercase text-white/50">Annual subscription</div>
            <div className="mt-2 flex items-baseline gap-2">
              {isCustom ? (
                <span className="text-2xl font-semibold text-emerald-300">Contact sales</span>
              ) : (
                <>
                  <span className="text-2xl font-semibold text-white tracking-tight">{formatMoney(annual)}</span>
                  <span className="text-white/50 text-sm">/yr</span>
                </>
              )}
            </div>
          </div>

          {/* Breakdown */}
          <div className="mt-6 space-y-2 text-[13px]">
            <div className="flex items-center justify-between text-white/70 gap-3">
              <span>Base · {tier.name}</span>
              <span className="text-white/90 tabular-nums whitespace-nowrap">{formatMoney(tier.price)}/mo</span>
            </div>
            {assetPacks > 0 && (
              <div className="flex items-center justify-between text-white/70 gap-3">
                <span>+ {assetPacks}× asset packs</span>
                <span className="text-white/90 tabular-nums whitespace-nowrap">{formatMoney(assetPacks * PTI_ASSETS_PACK.assetPackPrice)}/mo</span>
              </div>
            )}
            {userPacks > 0 && (
              <div className="flex items-center justify-between text-white/70 gap-3">
                <span>+ {userPacks}× user packs</span>
                <span className="text-white/90 tabular-nums whitespace-nowrap">{formatMoney(userPacks * PTI_ASSETS_PACK.userPackPrice)}/mo</span>
              </div>
            )}
            {billing === 'annual' && (
              <div className="flex items-center justify-between text-emerald-300 gap-3 pt-1 border-t border-white/5">
                <span>Annual discount −15%</span>
                <span className="tabular-nums whitespace-nowrap">−{formatMoney(monthly * PTI_ASSETS_ANNUAL_DISCOUNT)}/mo</span>
              </div>
            )}
          </div>

          {/* Grand total year 1 */}
          <div className="mt-6 rounded-xl border border-cyan-400/25 bg-cyan-400/[0.05] p-4">
            <div className="text-[10px] tracking-[0.22em] font-semibold uppercase text-cyan-200">Year 1 total</div>
            <div className="mt-3 flex items-center justify-between text-[13px] text-white/70">
              <span>Subscription (12 mo)</span>
              <span className="text-white tabular-nums">{isCustom ? 'Custom' : formatMoney(annual)}</span>
            </div>
            {includeConsulting && (
              <div className="mt-1 flex items-center justify-between text-[13px] text-white/70">
                <span>+ Consulting (optional)</span>
                <span className="text-white tabular-nums">{formatMoney(consultingTotal)}</span>
              </div>
            )}
            <div className="mt-3 pt-3 border-t border-cyan-400/20 flex items-center justify-between">
              <span className="text-[13px] font-semibold text-white/80">Total to pay</span>
              <span className="text-[24px] font-bold tabular-nums bg-clip-text text-transparent"
                style={{ backgroundImage: 'linear-gradient(90deg, #22d3ee 0%, #a855f7 100%)' }}>
                {isCustom ? 'Custom' : formatMoney(totalYearOne)}
              </span>
            </div>
          </div>

          {/* Protocols summary */}
          <div className="mt-5 rounded-xl border border-white/10 bg-white/[0.02] p-4">
            <div className="text-[10px] tracking-[0.22em] font-semibold uppercase text-white/50 mb-2">Real-time protocols</div>
            {selectedProtocols.length === 0 ? (
              <div className="text-[12.5px] text-yellow-300 flex items-center gap-2">
                <Info className="w-3.5 h-3.5" /> Pick at least one to continue
              </div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {selectedProtocols.map((p) => {
                  const proto = PTI_ASSETS_PROTOCOLS.find((x) => x.id === p);
                  return (
                    <span key={p} className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-2 py-1 rounded-full border border-cyan-400/30 bg-cyan-400/10 text-cyan-200">
                      <ProtocolIcon id={p} className="w-3 h-3" />
                      {proto.name}
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          <div className="mt-5 grid grid-cols-[1fr_auto] gap-3">
            <button
              onClick={proceedToQuote}
              className="h-11 rounded-lg bg-cyan-400 hover:bg-cyan-300 text-black font-semibold text-sm flex items-center justify-center gap-2 transition-colors shadow-[0_10px_30px_-10px_rgba(34,211,238,0.6)]"
            >
              <Save className="w-4 h-4" /> Build quotation
            </button>
            <button className="h-11 px-4 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-white/80 font-semibold text-sm flex items-center justify-center gap-2 transition-colors">
              <Mail className="w-4 h-4" /> Email
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
};

const StatTile = ({ icon: Icon, label, value }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
    <div className="flex items-center gap-2 text-[10px] tracking-[0.18em] font-semibold uppercase text-white/50">
      <Icon className="w-3.5 h-3.5 text-white/40" />
      {label}
    </div>
    <div className="mt-3 text-2xl font-semibold text-white tabular-nums">
      {typeof value === 'number' ? value.toLocaleString('en-US') : value}
    </div>
  </div>
);

export default PtiAssetsMode;
